import { useEffect, useRef, useState } from "react";
import { Label } from "reactstrap";

const CkEditor = ({
  id,
  label,
  initialData = "",
  onChange,
  onBlur,
  validationError,
}) => {
  const editorElementRef = useRef(null);
  const editorInstanceRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onBlurRef = useRef(onBlur);
  const [error, setError] = useState("");

  onChangeRef.current = onChange;
  onBlurRef.current = onBlur;

  // Initialize CKEditor
  useEffect(() => {
    let disposed = false;

    const initialize = async () => {
      try {
        if (typeof window.createCkEditor !== "function") {
          throw new Error("CKEditor configuration did not load.");
        }

        const editor = await window.createCkEditor(
          editorElementRef.current
        );

        if (disposed) {
          await editor.destroy();
          return;
        }

        editorInstanceRef.current = editor;

        // Set initial value
        editor.setData(initialData || "");

        editor.model.document.on("change:data", () => {
          onChangeRef.current?.(editor.getData(), editor);
        });

        editor.ui.focusTracker.on(
          "change:isFocused",
          (_, __, isFocused) => {
            if (!isFocused) {
              onBlurRef.current?.();
            }
          }
        );
      } catch (initializationError) {
        if (!disposed) {
          setError(
            initializationError.message || "Unable to load CKEditor."
          );
        }
      }
    };

    initialize();

    return () => {
      disposed = true;

      const editor = editorInstanceRef.current;
      editorInstanceRef.current = null;

      if (editor) {
        editor.destroy().catch(console.error);
      }
    };
  }, []);

  // IMPORTANT:
  // Update CKEditor whenever initialData changes
  useEffect(() => {
    const editor = editorInstanceRef.current;

    if (!editor) {
      return;
    }

    const currentData = editor.getData();

    if (currentData !== (initialData || "")) {
      editor.setData(initialData || "");
    }
  }, [initialData]);

  return (
    <div className="editorWrapper">
      <Label className="form-label" htmlFor={id}>
        {label}
      </Label>

      {error && (
        <p className="editor-error" role="alert">
          {error}
        </p>
      )}

      <div id={id} ref={editorElementRef} />

      {validationError && (
        <p className="field-error" role="alert">
          {validationError}
        </p>
      )}
    </div>
  );
};

export default CkEditor;

